import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Scale, FileText, TrendingUp, PoundSterling, Loader2, Download, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";

/**
 * Transparency & Governance — central financial-transparency hub demanded by
 * UK CIO / Charity Commission good practice and donor confidence.
 *
 * Sections:
 *  • Income summary (donations, membership, grants, sponsorship)
 *  • Last-12-months chart (donations vs membership stacked bars)
 *  • Donation allocation breakdown ("Where your donation goes")
 *  • Governance structure + committee panel
 *  • Documents library (Constitution, Safeguarding, E&D, Conflict of Interest, Code of Conduct)
 */

const TRUSTEES_PLACEHOLDER = [
  {
    name: "Mr Prafulla Ramteke",
    role: "Founder Trustee / Chair",
    bio: "Strategic leadership of the CIO. Chairs trustee meetings and the AGM. Primary contact for the Charity Commission. Joint and several responsibility for ensuring the charity acts within its objects. Volunteer; unpaid.",
  },
  {
    name: "Miss Siddhi Dhiwar",
    role: "Trustee / Secretary",
    bio: "Statutory record-keeping. Minutes of trustee meetings and general meetings. Statutory filings with the Charity Commission. Maintains the Register of Members and the Register of Interests. Volunteer; unpaid.",
  },
  {
    name: "Mr Vishwas Dhiwar",
    role: "Trustee",
    bio: "Ambedkarite community engagement and Buddhist organisational liaison. Contributes lived community experience to trustee decisions. Joint and several responsibility for ensuring the charity acts within its objects. Volunteer; unpaid.",
  },
];

const OPERATIONAL_ROLES = [
  { role: "Safeguarding Lead", summary: "Designated Safeguarding Lead for children and adults at risk. Reports to the trustees." },
  { role: "Youth Lead", summary: "Coordinates the youth wing (ages 13–25) and KS3/KS4 educational events." },
  { role: "Education Lead", summary: "Curates the Dhamma curriculum and KS1–KS4 learning materials." },
  { role: "Community Outreach", summary: "Liaison with partner Buddhist groups, universities and faith forums across the UK." },
  { role: "Volunteers", summary: "Event hosts, translators, editors, mentors, and mutual-aid coordinators." },
];

export default function Transparency() {
  const [finances, setFinances] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/public/finances");
        setFinances(r.data);
      } catch (e) { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  const totalIncome = finances ? (
    (finances.income.donations_total || 0) +
    (finances.income.membership_total || 0) +
    (finances.income.grants_total || 0) +
    (finances.income.sponsorship_total || 0)
  ) : 0;

  return (
    <div className="bg-pearl">
      <section className="bg-gradient-to-b from-navy via-navy to-navy-light text-pearl">
        <div className="section-container py-16 sm:py-20">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-sm bg-saffron/15 border border-saffron/40 text-saffron mb-6">
            <Scale size={24} />
          </div>
          <p className="eyebrow text-saffron">Transparency & Governance</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-4xl">
            Every pound, every pillar, in plain view.
          </h1>
          <p className="mt-6 text-pearl/80 max-w-3xl leading-relaxed text-lg">
            ABGN-UK believes donor trust must be earned with daylight. This page sets out
            how money flows in, where it goes, who governs the society and which
            policies bind our work.
          </p>
          <p className="mt-3 text-xs text-pearl/55 max-w-3xl">
            Figures update automatically from our payment system. Last audited: {finances?.last_audit_date || "annual audit pending"}.
          </p>
          <div className="mt-6">
            <Link
              to="/governance"
              data-testid="transparency-governance-link"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-saffron text-navy rounded-md text-sm font-medium hover:bg-saffron-light"
            >
              <FileText size={15} /> Open the Governance Book →
            </Link>
            <span className="ml-3 text-xs text-pearl/55">Constitution, policies, trustees & CIO pack PDF</span>
          </div>
        </div>
      </section>

      {/* INCOME SUMMARY */}
      <section className="py-16">
        <div className="section-container">
          <div className="max-w-3xl">
            <p className="eyebrow">Income</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl text-navy">How the Sangha is funded.</h2>
          </div>
          {loading ? (
            <div className="text-center py-12"><Loader2 size={20} className="animate-spin mx-auto text-slate-500" /></div>
          ) : (
            <>
              <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="transparency-income-grid">
                <IncomeCard label="Donations" value={finances?.income.donations_total} total={totalIncome} testid="income-donations" />
                <IncomeCard label="Membership" value={finances?.income.membership_total} total={totalIncome} testid="income-membership" />
                <IncomeCard label="Grants" value={finances?.income.grants_total} total={totalIncome} testid="income-grants" />
                <IncomeCard label="Sponsorship" value={finances?.income.sponsorship_total} total={totalIncome} testid="income-sponsorship" />
              </div>
              <div className="mt-3 text-right text-xs text-slate-500">
                Total income: <strong className="text-navy">£{totalIncome.toFixed(2)}</strong> · currency: GBP
              </div>

              {/* 12-month chart */}
              <div className="mt-12 bg-white border border-navy/10 rounded-sm p-6" data-testid="transparency-chart">
                <div className="flex items-baseline justify-between flex-wrap gap-2 mb-6">
                  <h3 className="font-display text-xl text-navy">Last 12 months</h3>
                  <div className="text-xs text-slate-500 inline-flex items-center gap-3">
                    <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 bg-saffron"></span>Donations</span>
                    <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 bg-navy"></span>Membership</span>
                  </div>
                </div>
                <BarChart data={finances?.monthly || []} />
              </div>
            </>
          )}
        </div>
      </section>

      {/* ALLOCATION */}
      <section className="py-16 bg-white">
        <div className="section-container">
          <div className="max-w-3xl">
            <p className="eyebrow text-saffron">Where your donation goes</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl text-navy">Spending breakdown.</h2>
            <p className="mt-4 text-slate-700 leading-relaxed">
              The trustee committee reviews this allocation annually and updates it on this page.
              Percentages are typical commitments; actual spend will vary month to month based on
              community need.
            </p>
          </div>
          <div className="mt-10 space-y-3" data-testid="transparency-allocation">
            {(finances?.allocation || []).map((a, i) => (
              <motion.div
                key={a.category}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="bg-pearl border border-navy/10 rounded-sm p-5"
                data-testid={`allocation-${i}`}
              >
                <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
                  <h3 className="font-display text-lg text-navy">{a.category}</h3>
                  <span className="font-display text-xl text-saffron">{a.percent}%</span>
                </div>
                <div className="h-2 bg-navy/10 rounded-sm overflow-hidden">
                  <div className="h-full bg-saffron transition-all" style={{ width: `${a.percent}%` }} />
                </div>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{a.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* GOVERNANCE / TRUSTEES */}
      <section className="py-16">
        <div className="section-container">
          <div className="max-w-3xl">
            <p className="eyebrow">Governance</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl text-navy">Who steers the Sangha.</h2>
            <p className="mt-4 text-slate-700 leading-relaxed">
              ABGN-UK is led by a volunteer trustee board. All trustees serve without
              remuneration and bear joint and several responsibility for the proper
              administration of the charity. A separate operational team — none of
              whom are trustees — runs day-to-day activities under trustee oversight.
            </p>
            <p className="mt-3 text-xs text-saffron uppercase tracking-[0.2em]">
              Charity registration in progress · CIO application in preparation
            </p>
          </div>

          <h3 className="mt-10 font-display text-xl text-navy">Trustee board (3)</h3>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="trustee-board">
            {TRUSTEES_PLACEHOLDER.map((t, i) => (
              <div key={i} className="bg-white border border-navy/10 rounded-sm p-6" data-testid={`trustee-${i}`}>
                <div className="text-[10px] uppercase tracking-[0.25em] text-saffron mb-1">{t.role}</div>
                <h3 className="font-display text-lg text-navy">{t.name}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{t.bio}</p>
              </div>
            ))}
          </div>

          <h3 className="mt-12 font-display text-xl text-navy">Operational volunteer roles</h3>
          <p className="mt-1 text-xs text-slate-500 italic">
            Appointed by, and accountable to, the trustee board. These roles are not trustees.
          </p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="operational-roles">
            {OPERATIONAL_ROLES.map((r) => (
              <div key={r.role} className="bg-pearl border border-navy/10 rounded-sm p-4">
                <div className="font-display text-sm text-navy">{r.role}</div>
                <p className="mt-1 text-xs text-slate-600 leading-relaxed">{r.summary}</p>
              </div>
            ))}
          </div>

          <p className="mt-6 text-xs text-slate-500 italic">
            Trustees declare conflicts of interest at the start of each meeting per our Conflict of Interest policy.
          </p>
        </div>
      </section>

      {/* DOCUMENTS */}
      <section className="py-16 bg-navy text-pearl">
        <div className="section-container max-w-5xl">
          <h2 className="font-display text-3xl sm:text-4xl text-pearl">Policies & documents</h2>
          <p className="mt-3 text-pearl/75 max-w-2xl">
            Read the policies that govern how we run this Sangha.
          </p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="transparency-policies">
            <DocLink to="/mission" title="Mission, Vision & Values" testid="doc-mission" />
            <DocLink to="/public-benefit" title="Public Benefit Statement" testid="doc-pb" />
            <DocLink to="/impact" title="Community Impact Report" testid="doc-impact" />
            <DocLink to="/safeguarding" title="Safeguarding Policy" testid="doc-safeguarding" />
            <DocLink to="/equality-diversity" title="Equality & Diversity" testid="doc-eandd" />
            <DocLink to="/conflict-of-interest" title="Conflict of Interest Policy" testid="doc-coi" />
            <DocLink to="/code-of-conduct" title="Code of Conduct" testid="doc-coc" />
            <DocLink to="/privacy" title="Data Privacy (GDPR)" testid="doc-privacy" />
            <DocLink to="/complaints" title="Complaints Procedure" testid="doc-complaints" />
            <DocLink to="/constitution" title="Constitution" testid="doc-constitution" />
          </div>
          <div className="mt-12 flex flex-wrap gap-3">
            <Link to="/volunteer" className="px-5 py-2.5 bg-saffron/15 border border-saffron/40 text-saffron rounded-sm text-sm hover:bg-saffron/25" data-testid="transparency-volunteer-link">
              Help us improve transparency &rarr; Volunteer
            </Link>
            <Link to="/donate" className="px-5 py-2.5 bg-pearl text-navy rounded-sm text-sm hover:bg-pearl/90" data-testid="transparency-donate-link">
              Donate with confidence
            </Link>
          </div>
        </div>
      </section>

      <section className="py-12 bg-pearl">
        <div className="section-container text-center max-w-2xl">
          <ShieldCheck size={28} className="text-saffron mx-auto mb-3" />
          <p className="text-sm text-slate-700 leading-relaxed">
            Annual accounts will be filed with Companies House (or the Charity Commission, where appropriate)
            and made downloadable from this page once the first financial year is complete.
            Donors will receive an annual impact letter summarising income, expenditure and outcomes.
          </p>
        </div>
      </section>
    </div>
  );
}

function IncomeCard({ label, value, total, testid }) {
  const v = Number(value || 0);
  const pct = total > 0 ? Math.round((v / total) * 100) : 0;
  return (
    <div className="bg-white border border-navy/10 rounded-sm p-5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-navy/55 inline-flex items-center gap-1">
        <PoundSterling size={11} /> {label}
      </div>
      <div className="mt-2 font-display text-3xl text-navy leading-none">£{v.toFixed(0)}</div>
      <div className="mt-2 h-1 bg-navy/10 rounded-sm overflow-hidden">
        <div className="h-full bg-saffron" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-1.5 text-[10px] text-slate-500">{pct}% of income</div>
    </div>
  );
}

function BarChart({ data }) {
  const maxVal = Math.max(1, ...data.map((d) => (d.donations || 0) + (d.membership || 0)));
  return (
    <div className="flex items-end gap-2 sm:gap-3 h-44 sm:h-56" aria-label="Income chart, last 12 months">
      {data.map((d) => {
        const total = (d.donations || 0) + (d.membership || 0);
        const h = total > 0 ? (total / maxVal) * 100 : 2;
        const donH = total > 0 ? (d.donations / total) * 100 : 0;
        const memH = total > 0 ? (d.membership / total) * 100 : 0;
        return (
          <div key={d.month} className="flex-1 flex flex-col items-center gap-1 min-w-0" title={`${d.month}: £${total.toFixed(2)}`}>
            <div className="flex-1 w-full flex items-end">
              <div className="w-full flex flex-col-reverse rounded-t-sm overflow-hidden border-t border-l border-r border-navy/5" style={{ height: `${h}%` }}>
                {d.donations > 0 && <div className="bg-saffron" style={{ height: `${donH}%`, minHeight: total ? 2 : 0 }} />}
                {d.membership > 0 && <div className="bg-navy" style={{ height: `${memH}%`, minHeight: total ? 2 : 0 }} />}
              </div>
            </div>
            <div className="text-[8px] sm:text-[10px] text-slate-500 uppercase tracking-wider truncate w-full text-center">
              {d.month.slice(5)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DocLink({ to, title, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="flex items-center justify-between gap-3 bg-pearl/5 border border-pearl/15 hover:border-saffron/50 hover:bg-pearl/10 px-4 py-3 rounded-sm transition-colors group"
    >
      <span className="inline-flex items-center gap-2 text-sm text-pearl">
        <FileText size={14} className="text-saffron/70" />
        {title}
      </span>
      <Download size={12} className="text-pearl/40 group-hover:text-saffron transition-colors" />
    </Link>
  );
}
